import { Outlet } from 'react-router-dom';
import { TimerOff } from 'lucide-react';
import { useWorkSessionMonitor } from '@/hooks/useWorkSession';
import { useRealtime } from '@/hooks/useRealtime';
import { Button, Modal } from '@/components/ui';
import { CreateModals } from '@/modules/shared/CreateModals';
import { Sidebar } from './Sidebar';
import { Topbar, WorkSessionPill } from './Topbar';
import { Suspense } from 'react';
import { Spinner } from '@/components/ui';
import { useAuth } from '@/store/auth';
import { CommandPalette } from './CommandPalette';

function IdleWarning() {
  const { showWarning, secondsLeft, stayActive } = useWorkSessionMonitor();
  return (
    <Modal
      open={showWarning}
      onClose={stayActive}
      size="sm"
      title="Are you still there?"
      footer={<Button onClick={stayActive}>I’m still working</Button>}
    >
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-warning-soft text-warning"><TimerOff className="h-5 w-5" /></span>
        <p className="text-sm text-ink-muted">
          You haven’t interacted with StopLord for a while. Under your company’s idle policy you will be signed out in{' '}
          <strong className="tabular text-ink">{secondsLeft}s</strong>. Idle time is not counted as active working time.
        </p>
      </div>
    </Modal>
  );
}

/** On phones the working-session status gets its own row so it is always visible. */
function MobileSessionBar() {
  const tracked = useAuth((s) => !!s.user?.tracksWorkTime && !!s.workSession && s.workSession.state !== 'ended');
  if (!tracked) return null;
  return <div className="border-b border-line bg-surface px-4 py-2 sm:hidden"><WorkSessionPill mobile /></div>;
}

export function AppLayout() {
  useRealtime();
  return (
    <div className="flex min-h-dvh">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[70] focus:rounded-lg focus:bg-surface focus:px-3 focus:py-2">Skip to content</a>
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar />
        <MobileSessionBar />
        <main id="main" className="mx-auto w-full max-w-[1400px] flex-1 px-4 py-6 sm:px-6">
          <Suspense fallback={<Spinner />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
      <CommandPalette />
      <IdleWarning />
      <CreateModals />
    </div>
  );
}
